import React, { useState, useRef } from "react";
import { useEffect } from "react";
import {auth} from "./firebase";
import { onAuthStateChanged, signOut} from "firebase/auth";
import {db} from "./firebase";
import {collection,query,orderBy,onSnapshot, addDoc, deleteDoc, doc} from "firebase/firestore";
import { Loader } from "lucide-react";

// MAIN COMPONENTS
import Header from "./components/Header";
import LeftPanel from "./components/LeftPanel";
import RecommendedList from "./components/RecommendedList";
import ShoppingList from "./components/ShoppingList";
import ChatbotModal from "./components/Chatbot";
import HistoryModal from "./components/HistoryModal";
import ItemDetailModal from "./components/ItemDetailModal";
import AddItemModal from "./components/AddItemModal";
import SubsidyNotification from "./SubsidyNotification";

// AUTH
import Welcome from "./Welcome";

// CATEGORY LABELS
const categoryLabels = {
  staple: "Staple Foods",
  quick: "Quick Food",
  dairy: "Dairy & Protein",
  beverages: "Drink & Beverages",
  canned: "Canned Foods",
  personal: "Personal Care & Health",
  household: "Household Supplies",
  school: "School & Stationery",
};

function LoadingOverlay(){
  return (
    <div className="fixed inset-0 z-50 flex flex-col items-center justify-center bg-black/60 backdrop-blur-sm transition-all">
      <div className="bg-white p-6 rounded-2xl shadow-2xl flex flex-col items-center space-y-4 animate-in fade-in zoom-in duration-300">
        
        {/* Spinning Icon */}
        <div className="relative">
          <div className="w-16 h-16 border-4 border-[#E8D1B7] border-t-[#FF8C42] rounded-full animate-spin"></div>
          <div className="absolute inset-0 flex items-center justify-center">
            <Loader className="w-6 h-6 text-[#FF8C42]" />
          </div>
        </div>

        {/* Text */}
        <div className="text-center">
          <h3 className="text-xl font-bold text-[#5A3E2B]">Analyzing Prices...</h3>
          <p className="text-gray-500 text-sm mt-1">Finding the best deals for you</p>
        </div>
        
      </div>
    </div>
  );
}

function App() {
  const [page, setPage] = useState("setup"); // setup | combinations | selected

  // SETUP STATES
  const [budget, setBudget] = useState("");
  const [selectedStore, setSelectedStore] = useState("all");
  const [selectedOptions, setSelectedOptions] = useState({});

  //AUTH STATES
  const[appStage, setAppStage] = useState("welcome");
  const[user, setUser] = useState(null);
  const[loading, setLoading] = useState(true);

  //LOADING SCREEN
  const[isGenerating, setIsGenerating] = useState(false);

  //FOR LOGIN STATUS
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (currentUser) => {
      if (currentUser) {
        //user login go to app
        setUser(currentUser);
        setAppStage("setup"); //automatically goes to APP when logged in
      } else {
        //user logout go to welcome
        setUser(null);
        setAppStage("welcome");
      }
      setLoading(false);
    });
    return() => unsubscribe();
  }, []);

  //LOGOUT FUNCTION
  const handleLogout = async () => {
    await signOut(auth);

    // 1. Wipe the save data
    sessionStorage.removeItem("budgetSession");

    // 2. Reset all data states to default
    setBudget("");
    setSelectedStore("all");
    setSelectedOptions({});
    setGeneratedCombinations([]);
    setCurrentCombination(null);
    setSelectedItem(null);

    // 3. Reset UI states
    setPage("setup");
    setShowHistory(false);
    setShowChatbot(false);

    // ----------------------------------------------------
    // 🔥 NEW ADDITION: FORCE CHAT TO RESET 🔥
    // This wipes the previous user's messages instantly.
    // ----------------------------------------------------
    setChatMessages([
      { role: "bot", text: "Hi! I'm your grocery assistant." },
    ]);
    
    // 4. Go to welcome screen
    setAppStage("welcome");
  };

  // RESULTS
  const [selectedModule, setSelectedModule] = useState("best_value");
  const [generatedCombinations, setGeneratedCombinations] = useState([]);
  const [currentCombination, setCurrentCombination] = useState(null);

  // MODALS
  const [selectedItem, setSelectedItem] = useState(null);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showChatbot, setShowChatbot] = useState(false);
  const [showHistory, setShowHistory] = useState(false);
  const isModalOpen = showHistory || showAddModal || showChatbot || selectedItem;

  // ============================
  // PERSISTENCE (SAVE STATE)
  // ============================
  useEffect(() => {

    //if user is logged out, do not save anything.
    if (!user) return;

    // Only save if have a data (don't save empty states on initial load)
    if (budget || selectedStore !== "all" || Object.keys(selectedOptions).length > 0) {
      const sessionData = {
        budget,
        selectedStore,
        selectedOptions,
        page,
        generatedCombinations,
        selectedModule
      };
      sessionStorage.setItem("budgetSession", JSON.stringify(sessionData));
    }
  }, [budget, selectedStore, selectedOptions, page, generatedCombinations, selectedModule, user]);

  // ============================
  // PERSISTENCE (LOAD STATE)
  // ============================
  useEffect(() => {
    const savedData = sessionStorage.getItem("budgetSession");
    if (savedData) {
      try {
        const parsed = JSON.parse(savedData);
        
        // Restore inputs
        if (parsed.budget) setBudget(parsed.budget);
        if (parsed.selectedStore) setSelectedStore(parsed.selectedStore);
        if (parsed.selectedOptions) setSelectedOptions(parsed.selectedOptions);
        if (parsed.selectedModule) setSelectedModule(parsed.selectedModule);

        // Restore results if they exist
        if (parsed.generatedCombinations && parsed.generatedCombinations.length > 0) {
          setGeneratedCombinations(parsed.generatedCombinations);
        }

        // Restore page location (Setup vs Combinations)
        if (parsed.page) setPage(parsed.page);

      } catch (e) {
        console.error("Failed to load session", e);
      }
    }
  }, []);

  // CACHE RESULTS
  const [resultsCache, setResultsCache] = useState({});
  useEffect(() => {
    setResultsCache({}); //clean the cache
  }, [budget, selectedStore, selectedOptions]);

  // POPUP
  const [showSavedPopup, setShowSavedPopup] = useState(false);
  const [popupMessage, setPopupMessage] = useState("");

  // CHATBOT
  const [chatMessages, setChatMessages] = useState([
    { role: "bot", text: "Hi! I'm your grocery assistant." },
  ]);
  const [userInput, setUserInput] = useState("");
  const chatEndRef = useRef(null);

  const handleChatSubmit = async () => {
    if (!userInput.trim()) return;

    const currentInput = userInput;
    setUserInput("");

    // 1. Show User Message
    setChatMessages((prev) => [
      ...prev,
      { role: "user", text: currentInput },
    ]);

    try {
      // 2. Send to Backend (Simple, no 'mode' param)
      const res = await fetch("https://smart-budget-ke77.onrender.com/api/chatbot", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ message: currentInput }), 
      });

      const data = await res.json();

      // 3. Show Bot Response
      setChatMessages((prev) => [
        ...prev,
        { role: "bot", text: data.text },
      ]);

    } catch (error) {
      console.error("Connection Error:", error);
      setChatMessages((prev) => [
        ...prev,
        { role: "bot", text: "⚠️ Error: Server is offline." },
      ]);
    }
  };

  // HISTORY
  const [savedHistory, setSavedHistory] = useState([]);
  useEffect(() => {
    if (user) {
      // Listen to the user's history collection in real-time
      const q = query(collection(db, "users", user.uid, "history"), orderBy("date", "desc"));
      const unsubscribe = onSnapshot(q, (snapshot) => {
        const historyData = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
        setSavedHistory(historyData);
      });
      return () => unsubscribe();
    } else {
      setSavedHistory([]);
    }
  }, [user]);

  // ============================
  // SAVE TO HISTORY + POPUP
  // ============================
  const saveToHistory = async (comboObj) => {
    if (!user) {
    setPopupMessage("⚠️ Login to save history");
    setShowSavedPopup(true);
    setTimeout(() => setShowSavedPopup(false), 2000);
    return;
    }

    try {
      await addDoc(collection(db, "users", user.uid, "history"), {
        date: new Date().toISOString(),
        items: comboObj.items,
        total: comboObj.total,
        store: comboObj.store,
        budget: budget || 0
      });

      setPopupMessage("✅ Saved to history");
      setShowSavedPopup(true);
      setTimeout(() => setShowSavedPopup(false), 2000);
    } catch (error) {
      console.error("Error saving history:", error);
    }
  };

  // ============================
  // LOAD / DELETE HISTORY
  // ============================
  const loadHistoryItem = (entry) => {
    setBudget(entry.budget);
    setSelectedStore(entry.store);
    setCurrentCombination(entry.items);
    setGeneratedCombinations([]);
    setPage("selected");
    setShowHistory(false);
  };

  const deleteHistoryItem = async (id) => {
    if (!user) return;
    try {
      await deleteDoc(doc(db, "users", user.uid, "history", id));
    } catch (error) {
      console.error("Error deleting:", error);
    }
  };

  // ============================
  // GENERATE COMBINATIONS + Caching
  // ============================
  const generateBudgetList = async (targetMode = null, forceRefresh = false) => {
    const amount = parseFloat(budget);
    if (!amount) return;

    // Determine which mode are asking for (clicked button OR current active)
    const modeToUse = targetMode || selectedModule;

    // 1. CHECK CACHE FIRST
    // if we are not forcing a refresh, then we are already have data for this mode
    if (!forceRefresh && resultsCache[modeToUse]){
      console.log(`Using cached results for: ${modeToUse}`);
      setGeneratedCombinations(resultsCache[modeToUse]);
      setSelectedModule(modeToUse);
      setPage("combinations");
      return; // not call API
    }
    // Fetch from API (if no cache or refresh clicked)
    setIsGenerating(true);

    try {
      const res = await fetch("https://smart-budget-ke77.onrender.com/api/generate-combo", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          budget: amount,
          store: selectedStore,
          options: selectedOptions,
          mode: modeToUse, //
        }),
      });

      const data = await res.json();
      if (Array.isArray(data)) {
        // NEW (SAVE CACHE)
        setResultsCache(prev => ({
          ...prev,
          [modeToUse]: data // save these specific results under "economic", "best_value", etc.
        }));

        setGeneratedCombinations(data);
        setCurrentCombination(null);

        //only swith page if we are in setup or coming from elsewhere
        if (page === "setup") setPage("combinations");
      }
    } catch (error){
      alert("Backend error. Timeout");
      console.error(error);
    } finally {
      setIsGenerating(false);
    }
  };

  // ===========================
  // ADVANCED SORTING AND TOP PICK LOGIC
  // ===========================
  const getSortedCombinations = () => {
    if (!generatedCombinations.length) return [];

    //filter out anything over budget first (safety check)
    const validCombos =generatedCombinations.filter(c => c.total <= parseFloat(budget));

    //if no valid combos exist under budget, return empty
    if (validCombos.length === 0) return [];

    //sort based on the selected module
    const sorted = [...validCombos].sort((a,b) => {
      switch (selectedModule){
        //economic: cheapest first 
        case "economic":
          return a.total - b.total;
        
        //max budget: most expensive (closest to budget)
        case "max_budget":
          return b.total - a.total;
        
        //variation: most unique categories first
        case "variation":
          //calculate how many unique categories are in combo A vs combo B
          const uniqueCatsA = new Set(a.items.map(item => item.category)).size;
          const uniqueCatsB = new Set(b.items.map(item => item.category)).size;

          //if diversity is the same, fallback to lower price
          if (uniqueCatsB === uniqueCatsA){
            return a.total - b.total
          }
          return uniqueCatsB - uniqueCatsA; //higher diversity wins
        
        case "best_value":
        default: 
          //formula: count/price. (higher is better)
          //example: 10 items for RM50(0.2 score) vs 5 items for RM40(0.125 score)
          const valueScoreA = a.items.length / a.total;
          const valueScoreB = b.items.length / b.total;

          //if scores are very similar, prefer the cheaper one
          if(Math.abs(valueScoreA - valueScoreB) < 0.001){
            return a.total - b.total;
          }
          return valueScoreB - valueScoreA; //higher score wins
      }
    });
    return sorted;
  };

  // RENDER LOADING SCREEN
  if (loading) {
    return <div className="h-screen flex items-center justify-center">Loading...</div>
  }

  // ============================
  // RENDER WELCOME
  // ============================
  if (appStage === "welcome") {
    return (
      <Welcome
        onStartGuest={() => setAppStage("setup")}
        onLoginSuccess={() => {
          console.log("🚀 Manual Login Triggered");
          setAppStage("setup");
        }}
      />
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#FFEFD5] via-[#FFE2BC] to-[#D6C7B0] p-4 sm:p-6 relative">
      
      {/*LOADING OVERLAY*/}
      {isGenerating && <LoadingOverlay/>}

      <div className={`max-w-7xl mx-auto transition-all duration-300
        ${isModalOpen ? "opacity-100 pointer-events-none blur-sm" : "opacity-100"}`}
      >
        <Header
          userEmail={user?.email}
          onToggleHistory={() => setShowHistory(!showHistory)}
          onToggleChat={() => setShowChatbot(!showChatbot)}
          onLogout={handleLogout}
        />

        {/* SETUP */}
       {page === "setup" && (
          <div className="mt-6">
            <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
              <LeftPanel
                budget={budget}
                setBudget={setBudget}
                selectedStore={selectedStore}
                setSelectedStore={setSelectedStore}
                selectedOptions={selectedOptions}
                selectAllOptions={setSelectedOptions}
                toggleOption={(opt) =>
                setSelectedOptions((prev) => ({ ...prev, [opt]: !prev[opt] }))}
                generateBudgetList={generateBudgetList}
                categoryLabels={categoryLabels}
              />
            </div>
          </div>
        )}


        {/* COMBINATIONS */}
        {page === "combinations" && (
          <div className="mt-6 space-y-6">
            <button
              onClick={() => setPage("setup")}
              className="px-4 py-2 rounded-xl bg-[#E8D1B7]"
            >
              ← Back to Setup
            </button>

            {/* MODULE BUTTONS */} 
            {/* FILTER TABS */}
            <div className="flex flex-wrap gap-3 bg-[#FFF7E6] border border-[#E8D1B7] p-4 rounded-xl">
              {[
                { id: "best_value", label: "Best Value", icon: "👍" },
                { id: "economic", label: "Economic", icon: "💲" },
                { id: "variation", label: "Variation", icon: "🎨" },
                { id: "max_budget", label: "Max Budget", icon: "📈" },
              ].map((m) => {
                const isActive = selectedModule === m.id;
                  
                return (
                  <button
                    key={m.id}
                    disabled={isActive} //Prevent Clicking again
                    onClick={() => {
                      setSelectedModule(m.id);
                      generateBudgetList(m.id, false); // 2. PASS NEW ID DIRECTLY
                    }}
                    className={`
                      px-4 py-2 rounded-xl border font-semibold transition-all duration-200
                      ${isActive 
                        ? "bg-[#38A3A5] text-white cursor-default shadow-inner opacity-100" // Active Look
                        : "bg-white hover:bg-[#FFF0D9] active:scale-95 text-gray-700"      // Inactive Look
                      }
                    `}
                  >
                    {m.icon} {m.label}
                  </button>
                );
              })}
            </div>

            <RecommendedList
              generatedCombinations={getSortedCombinations()}
              selectedModule={selectedModule}
              topPickIndex={0}
              budget={parseFloat(budget)}
              onRefresh={() => generateBudgetList(selectedModule, true)} //call API
              onSelect={(items) => {
                setCurrentCombination(items);
                setPage("selected");
              }}
            />
          </div>
        )}

        {/* SELECTED ITEMS */}
        {page === "selected" && currentCombination && (
          <div className="mt-6 space-y-6">
            <button
              onClick={() => {
                if (generatedCombinations.length > 0) {
                  setPage("combinations");
                } else {
                  setPage("setup");
                }
              }}
              className="px-4 py-2 rounded-xl bg-[#E8D1B7]"
            >
              {generatedCombinations.length > 0 ? "← Back to Options" : "← Back to Options"}             
            </button>

            <ShoppingList
              currentCombination={currentCombination}
              selectedItem={selectedItem}
              setSelectedItem={setSelectedItem}
              budget={budget}
              onRemoveItem={(i) =>
                setCurrentCombination((prev) =>
                  prev.filter((_, idx) => idx !== i)
                )
              }
              onOpenAddModal={() => setShowAddModal(true)}
              onSave={() =>
                saveToHistory({
                  items: currentCombination,
                  total: currentCombination.reduce((sum, item) => sum + item.price,0),
                  store: selectedStore,
                })
              }
            />
          </div>
        )}
      </div>

      {/* MODALS */}
      <HistoryModal
        open={showHistory}
        onClose={() => setShowHistory(false)}
        savedHistory={savedHistory}
        loadHistoryItem={loadHistoryItem}
        deleteHistoryItem={deleteHistoryItem}
      />

      <ChatbotModal
        open={showChatbot}
        onClose={() => setShowChatbot(false)}
        chatMessages={chatMessages}
        userInput={userInput}
        setUserInput={setUserInput}
        handleChatSubmit={handleChatSubmit}   
        chatEndRef={chatEndRef}
      />


      <ItemDetailModal
        selectedItem={selectedItem}
        setSelectedItem={setSelectedItem}
      />

      <AddItemModal
        isOpen={showAddModal}
        onClose={() => setShowAddModal(false)}
        onAddItem={(item) =>
          setCurrentCombination((prev) => [...prev, item])
        }
      />

      {/* SAVE POPUP */}
      {showSavedPopup && (
        <div className="fixed inset-0 flex items-center justify-center z-50">
          <div className="bg-[#38A3A5] text-white px-8 py-4 rounded-2xl shadow-xl font-semibold text-lg">
            {popupMessage}
          </div>
        </div>
      )}
      <SubsidyNotification/>
    </div>
  );
}

export default App;
